import { Dialog, DialogContent, DialogActions, DialogTitle, Button, Grid2 } from '@mui/material'
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import MyAutoComplete from '../../../components/inputs/MyAutoComplete';
import { myApi } from '../../../config/axiosConfig';
import Swal from 'sweetalert2';
import img from '../../../assets/img/spinner.svg';

function CrearEditarModal({ open, onClose, refresh, selected, isEditing, refreshStops }) 
{
    const [productos, setProductos] = useState([]);
    const [relacion, setRelacion] = useState(null);
    const [relaciones, setRelaciones] = useState([]);
    const [loading, setLoading] = useState(false);

    const esEditar = relacion !== null;

    const [initialValues, setInitialValues] = useState({
        id_stop: '',
        id_producto: '',
        tipo: '',
        criterio: ''
    });

    const validationSchema = Yup.object({
        id_producto: Yup.number().required('El producto es requerido.'),
        tipo: Yup.string().oneOf(['NB', 'SB']).required('El tipo es requerido.'),
        criterio: Yup.number().min(1).max(5).required('El criterio es requerido.'),
    });

    const fetchDatosIniciales = async () => 
    {
        try 
        {
            setLoading(true);

            const [productosRes, evaluacionRes] = await Promise.all([
                myApi.get('loves/catalogos/productos'),
                myApi.get(`loves/stops/evaluacion/${selected?.id_stop}`)
            ]);

            setProductos(productosRes.data.data.map(p => ({
                value: p.productCode, label: p.productName
            })));

            const relacionesEvaluadas = Array.isArray(evaluacionRes.data.data) ? evaluacionRes.data.data : [];
            setRelaciones(relacionesEvaluadas);

            if (isEditing && selected.id) 
            {
                setRelacion(selected);
                setInitialValues({
                    id_stop: selected.truck_stop_code,
                    id_producto: selected.product_code,
                    tipo: selected.type,
                    criterio: selected.criteria
                });
            } 
            else 
            {
                setRelacion(null);
                setInitialValues({
                    id_stop: selected?.id_stop,
                    id_producto: '',
                    tipo: '',
                    criterio: ''
                });
            }
        } 
        catch(err) 
        {
            console.error('Error al cargar datos iniciales', err);
        } 
        finally 
        {
            setLoading(false);
        }
    };

    useEffect(() => 
    {
        if (selected)
            fetchDatosIniciales();
    }, [selected]);

    const submitForm = async (values, { setSubmitting }) => 
    {
        try 
        {
            const payload = 
            {
                id_stop: isEditing ? selected?.truck_stop_code : selected?.id_stop,
                id_producto: values.id_producto,
                tipo: values.tipo,
                criterio: values.criterio
            };

             const yaExiste = relaciones.some(r =>
                r.id_product === payload.id_producto &&
                r.type === payload.tipo &&
                r.id_stop === payload.id_stop &&
                (
                    !relacion || r.id !== relacion.id
                )
            );

            if (yaExiste) 
            {
                Swal.fire({
                    icon: 'warning',
                    title: 'Duplicado',
                    text: 'Ya existe esta combinación para esta stop.',
                    target: document.getElementById('dialog2')
                });
                setSubmitting(false);
                return;
            }

            let response;

            if (relacion)
                response = await myApi.put(`loves/stops/evaluacion/${relacion.id}`, payload);
            else
                response = await myApi.post(`loves/stops/evaluacion`, payload);

            if (response.data.success) 
            {
                if (relacion) 
                {
                    Swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'success',
                        title: 'Criterio actualizado',
                        showConfirmButton: false,
                        timer: 1500,
                        timerProgressBar: true
                    });
                    refresh();
                    refreshStops();
                    onClose();
                } 
                else 
                {
                    Swal.fire({
                        icon: 'success',
                        title: 'Éxito',
                        text: 'Criterio guardado',
                        target: document.getElementById('dialog2')
                    }).then(() => {
                        refresh();
                        refreshStops();
                        onClose();
                    });
                }
                return;
            }
            else
            {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'No se pudo guardar',
                    target: document.getElementById('dialog2')
                });
            }
        } 
        catch(err) 
        {
            console.error('Error al guardar', err);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Hubo un problema',
                target: document.getElementById('dialog2')
            });
        }
    };

    return (
        <Dialog id="dialog2" open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{isEditing ? 'Editar' : 'Crear'}</DialogTitle>
            <DialogContent>
                {loading || !productos.length ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
                        <img
                            alt="Cargando..."
                            width="100px"
                            height="100px"
                            src={img}
                            style={{ borderRadius: "50%" }}
                        />
                    </div>
                ) : (
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={submitForm}
                >
                    {({ handleSubmit, isSubmitting }) => (
                        <Form onSubmit={handleSubmit} style={{ width: '100%' }}>
                            <Grid2 container spacing={2}>
                                <Grid2 size={{xs: 12}}>
                                    <MyAutoComplete
                                        name="id_producto"
                                        label="Producto"
                                        options={productos}
                                        disabled={esEditar}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12}}>
                                    <MyAutoComplete
                                        name="tipo"
                                        label="Tipo"
                                        options={[
                                            { value: 'NB', label: 'NB' },
                                            { value: 'SB', label: 'SB' }
                                        ]}
                                        disabled={esEditar}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12}}>
                                    <MyAutoComplete
                                        name="criterio"
                                        label="Criterio"
                                        options={[
                                            { value: 1, label: 'Muy Bueno' },
                                            { value: 2, label: 'Bueno' },
                                            { value: 3, label: 'Regular' },
                                            { value: 4, label: 'Malo' },
                                            { value: 5, label: 'Muy Malo' },
                                        ]}
                                    />
                                </Grid2>
                                <DialogActions style={{ justifyContent: 'flex-end' }}>
                                    <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                                        {isSubmitting ? 'Guardando...' : 'Guardar'}
                                    </Button>
                                    <Button onClick={onClose}>Cancelar</Button>
                                </DialogActions>
                            </Grid2>
                        </Form>
                    )}
                </Formik>
                )}
            </DialogContent>
        </Dialog>
    )
}

export default CrearEditarModal